import {
  AfterContentInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChild,
  effect,
  input,
  output,
  viewChild,
} from '@angular/core';
import { CdkVirtualScrollViewport, ScrollingModule } from '@angular/cdk/scrolling';
import { NgTemplateOutlet } from '@angular/common';
import { BzTableColumnSettings } from './bz-table-column-manager';
import { BzTableColumnManagerDirective } from './bz-table-column-manager.directive';
import { BzTableHeaderDef, BzTableRowDef } from './bz-table-defs';
import { BzTableScrollSyncDirective } from './bz-table-scroll-sync.directive';

const DEFAULT_ROW_HEIGHT = 48;
const DEFAULT_HEADER_HEIGHT = 33;
const FETCH_NEXT_SCROLL_THRESHOLD = 50;

/**
 * One shared table: virtual scroll, empty state, optional column resize/reorder.
 *
 * The caller owns the markup: project a `bzTableHeader` template with the `.th` cells and a
 * `bzTableRow` template with the `.td` cells, both keyed by the same `col-<name>` classes as
 * `defaultWidths`. `[resizable]="false"` skips the column manager — no resize handles, no
 * drag-reorder.
 *
 *   <bz-table [uniqueId]="tableUniqueId" [defaultWidths]="defaultWidths"
 *             [items]="items" [resizable]="true" (fetchNext)="loadNextPage()">
 *     <ng-template bzTableHeader>
 *       <div class="th col-status">Status</div>
 *     </ng-template>
 *     <ng-template bzTableRow let-item>
 *       <div class="tr">
 *         <div class="td col-status">{{ item.status }}</div>
 *       </div>
 *     </ng-template>
 *     <bz-empty-state bzTableEmptyState title="Nothing found" />
 *   </bz-table>
 *
 * Paging is the caller's too: this component only reports that the body is scrolled near its
 * end through `fetchNext`, and renders whatever `items` it is given back. Every table this was
 * built for loads from a server-paginated source, so who holds the cursor and whether a new
 * page is appended or replaces the list is a decision that belongs with that source, not here.
 *
 * Ported from the console's `app-bringoz-data-table`. Two things changed on the way in: the
 * template's console layout utility classes (`layout-column`, `flex`, `full-height`) became
 * explicit CSS, because those classes are the console's and a consumer has none; and the
 * decorator inputs became signal inputs, which is safe here only because every call site is an
 * Angular template — signal inputs silently fail to bind across `downgradeComponent`.
 */
@Component({
  selector: 'bz-table',
  imports: [
    ScrollingModule,
    NgTemplateOutlet,
    BzTableColumnManagerDirective,
    BzTableScrollSyncDirective,
  ],
  templateUrl: './bz-table.html',
  styleUrl: './bz-table.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BzTable<T = unknown> implements AfterContentInit {
  /** Scopes the injected column stylesheet and the `bz-table-<uniqueId>` class — unique per instance. */
  readonly uniqueId = input.required<string>();
  readonly items = input<readonly T[]>([]);
  /** Key the row id is read from, for cdkVirtualFor's trackBy. */
  readonly idField = input('id');
  readonly rowHeight = input<number | undefined>(undefined);
  readonly headerHeight = input<number | undefined>(undefined);
  readonly isLoading = input(false);
  readonly hideWhenEmpty = input(false);
  /** When false, columns stay at their CSS widths — no resize handle, no drag-reorder. */
  readonly resizable = input(true);
  /** Initial width per column class, e.g. `{'col-status': 120}` — keyed like the projected cells. */
  readonly defaultWidths = input<Record<string, number>>({});
  readonly settings = input<BzTableColumnSettings>({ columnSizes: {}, columnOrder: [] });

  readonly settingsChange = output<BzTableColumnSettings>();
  readonly fetchNext = output<void>();

  protected readonly headerDef = contentChild(BzTableHeaderDef);
  protected readonly rowDef = contentChild(BzTableRowDef);

  private readonly viewport = viewChild(CdkVirtualScrollViewport);

  // Scroll fires many times while the viewport is still near the bottom. Without this, the same
  // page is requested twice before isLoading / the next cursor come back.
  private fetchNextIssued = false;

  // Last node that received mouseover inside the viewport. Scrolling moves rows under a still
  // pointer, so the browser never fires mouseleave — hover tooltips parked on document.body
  // then stay open. We replay mouseleave on this node from onBodyScroll.
  private lastHoverTarget: EventTarget | null = null;

  protected readonly tableClass = computed(() => `bz-table-${this.uniqueId()}`);
  protected readonly resolvedRowHeight = computed(() => this.rowHeight() || DEFAULT_ROW_HEIGHT);
  protected readonly resolvedHeaderHeight = computed(
    () => this.headerHeight() || DEFAULT_HEADER_HEIGHT,
  );
  protected readonly rowHeightPx = computed(() => `${this.resolvedRowHeight()}px`);
  protected readonly headerHeightPx = computed(() => `${this.resolvedHeaderHeight()}px`);
  protected readonly showEmptyState = computed(() => !this.isLoading() && !this.items().length);

  constructor() {
    // Replaces the ngOnChanges the console version used. Reading both signals is what
    // subscribes this effect to them.
    effect(() => {
      const items = this.items();
      const loading = this.isLoading();
      if (items || !loading) {
        this.fetchNextIssued = false;
      }

      // An effect runs before Angular writes this component's own template bindings to the
      // DOM, so on the very first items arriving together with hideWhenEmpty flipping
      // [hidden] false, this synchronous call still measures a display:none element (0
      // height) — CDK then renders almost no rows and never re-measures on its own
      // afterwards (it only listens for window resize, not for an ancestor's visibility
      // changing). The synchronous call is kept for tables that never toggle [hidden] and get
      // a correctly-sized element right away; the microtask-deferred one re-measures once
      // this cycle's DOM writes have landed.
      this.viewport()?.checkViewportSize();
      void Promise.resolve().then(() => this.viewport()?.checkViewportSize());
    });
  }

  // Both templates are required, and a missing one renders as an empty header or an empty body —
  // which reads as "no data" rather than as a wiring mistake. Say so instead.
  ngAfterContentInit(): void {
    if (!this.headerDef() || !this.rowDef()) {
      console.warn(
        `bz-table (${this.uniqueId()}): missing ` +
          `${!this.headerDef() ? '<ng-template bzTableHeader>' : ''}` +
          `${!this.headerDef() && !this.rowDef() ? ' and ' : ''}` +
          `${!this.rowDef() ? '<ng-template bzTableRow>' : ''} — that part of the table stays empty.`,
      );
    }
  }

  // Falls back to the index when the row has no id: an undefined key for every row would make
  // cdkVirtualFor treat all rows as the same item.
  protected readonly trackByFn = (index: number, item: T): unknown =>
    (item as Record<string, unknown>)?.[this.idField()] ?? index;

  protected onSettingsChange(settings: BzTableColumnSettings): void {
    // Deliberately not written back to the `settings` input: the column manager mutates the
    // object it was given, and handing a new reference back down would make it re-apply its
    // own change.
    this.settingsChange.emit(settings);
  }

  protected onBodyMouseOver(event: MouseEvent): void {
    this.lastHoverTarget = event.target;
  }

  protected onBodyScroll(): void {
    this._dismissHoverFromLastTarget();
    const viewport = this.viewport();
    if (!viewport || this.isLoading() || this.fetchNextIssued) {
      return;
    }
    if (viewport.measureScrollOffset('bottom') < FETCH_NEXT_SCROLL_THRESHOLD) {
      this.fetchNextIssued = true;
      this.fetchNext.emit();
    }
  }

  private _dismissHoverFromLastTarget(): void {
    const target = this.lastHoverTarget;
    this.lastHoverTarget = null;
    if (!(target instanceof Node)) {
      return;
    }
    target.dispatchEvent(new MouseEvent('mouseleave', {bubbles: true}));
  }
}
